import { Popover, PopoverContent, PopoverTrigger } from '@chakra-ui/popover';
import {
  CalendarIcon,
  CloseIcon,
  FilterIcon as FiltersIcon,
  MyTaskIcon,
  NumberIcon,
  SelectIcon,
  TextIcon,
} from '@sketchnote/icons';
import { Calendar, Checkbox } from '@sketchnote/shared-components';
import Tippy from '@tippyjs/react';
import classNames from 'classnames';
import * as DateFns from 'date-fns';
import { isNil } from 'lodash';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { DateRangeValue, FilterOption, FilterValue, NumberRangeValue } from '.';

export function TableFilters({
  filterOptions = [],
  onFilterChange,
  onFilterClear,
  activeFilters = {},
  onClearAllFilters,
}: {
  filterOptions: FilterOption[];
  onFilterChange: (filterId: string, value: FilterValue) => void;
  onFilterClear: (filterId: string) => void;
  activeFilters?: { [filterId: string]: FilterValue };
  onClearAllFilters?: () => void;
}) {
  const [showFilters, setShowFilters] = useState(false);
  const [filterSearch, setFilterSearch] = useState('');

  const searchInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setFilterSearch('');
    if (showFilters) {
      setTimeout(() => searchInputRef.current?.focus(), 100);
    }
  }, [showFilters]);

  const onAddFilter = (filterId: string) => {
    setShowFilters(false);
    const filterOption = filterOptions.find((option) => option.filterId === filterId);
    if (!filterOption) return;

    if (filterOption.filterType === 'select') {
      const filterValue = filterOption.filterOptions?.map((option) => option.value) || [];
      if (filterOption.includeUnassigned) filterValue.push('unassigned');
      onFilterChange(filterId, filterValue);
    } else if (filterOption.filterType === 'dateRange') {
      onFilterChange(filterId, { startDate: null, endDate: null, showUnassigned: true });
    } else if (filterOption.filterType === 'numberRange') {
      onFilterChange(filterId, { min: null, max: null });
    } else {
      onFilterChange(filterId, null);
    }

    /** handle open filter popup */
    setTimeout(() => {
      document.dispatchEvent(new CustomEvent('openFilterPopup', { detail: filterId }));
    }, 200);
  };

  const filteredFilters = useMemo(() => {
    return filterOptions.filter((filterOption) => {
      if (
        filterSearch &&
        !filterOption.filterLabel.toLowerCase().includes(filterSearch.toLowerCase())
      )
        return false;
      return true;
    });
  }, [filterSearch, filterOptions]);

  return (
    <div className="flex items-center gap-8">
      <div className="flex items-center gap-8 flex-wrap">
        {filterOptions.map((filterOption) => {
          const filterValue = activeFilters[filterOption.filterId];
          const isActive = Object.keys(activeFilters).includes(filterOption.filterId);
          if (!isActive) return <></>;
          return (
            <ActiveFilter
              key={filterOption.filterId}
              filterOption={filterOption}
              value={filterValue}
              onFilterChange={onFilterChange}
              onFilterClear={onFilterClear}
            />
          );
        })}
        {Object.keys(activeFilters).length > 1 && (
          <button
            className="px-10 py-4 border border-gray-200 rounded text-gray-600 text-sm"
            onClick={onClearAllFilters}
          >
            Clear all
          </button>
        )}
      </div>
      <Popover placement="bottom-end" isOpen={showFilters} onClose={() => setShowFilters(false)}>
        <PopoverTrigger>
          <button
            className="flex items-center px-10 py-4 border border-gray-200 rounded gap-6 text-gray-600 text-sm"
            onClick={() => setShowFilters(!showFilters)}
          >
            <FiltersIcon className="py-1" size={21} />
            {!Object.keys(activeFilters).length && <span>Filter</span>}
          </button>
        </PopoverTrigger>
        <PopoverContent>
          <div className="bg-white rounded-lg border border-gray-200 min-w-200 gap-4 text-left">
            <div className="border-b border-gray-200">
              <input
                className="px-12 py-6"
                type="text"
                placeholder="Search"
                value={filterSearch}
                onChange={(e) => setFilterSearch(e.target.value)}
                ref={searchInputRef}
              />
            </div>
            <div className="flex flex-col gap-4 p-4  max-h-450 overflow-x-auto">
              {filteredFilters.map((filterOption) => {
                return (
                  <span
                    key={filterOption.filterId}
                    onClick={onAddFilter.bind(this, filterOption.filterId)}
                    className="flex items-center gap-8 cursor-pointer hover:bg-gray-100 px-8 py-8 rounded text-gray-900"
                  >
                    {/* <FilterIcon filterOption={filterOption} /> */}
                    {filterOption.filterLabel}
                  </span>
                );
              })}
              {!filteredFilters.length && (
                <span className="text-gray-600 text-center text-sm">No filters found</span>
              )}
            </div>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}

const ActiveFilter = ({
  filterOption,
  onFilterChange,
  onFilterClear,
  value,
}: {
  filterOption: FilterOption;
  onFilterChange: (filterId: string, value: FilterValue) => void;
  onFilterClear: (filterId: string) => void;
  value: FilterValue;
}) => {
  const [showFilterContent, setShowFilterContent] = React.useState(false);
  const [filterValue, setFilterValue] = React.useState<FilterValue>(value);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleApplyFilter = () => {
    onFilterChange(filterOption.filterId, filterValue);
    setShowFilterContent(false);
  };

  const handleToggleFilterPopup = () => {
    if (showFilterContent) {
      handleApplyFilter();
      return;
    } else {
      setFilterValue(value);
      setShowFilterContent(true);
    }
  };

  const handleEsc = useCallback((e: KeyboardEvent) => {
    if (e.key === 'Escape') setShowFilterContent(false);
  }, []);

  useEffect(() => {
    document.addEventListener('keyup', handleEsc);
    return () => {
      document.removeEventListener('keyup', handleEsc);
    };
  }, [handleEsc]);

  const onFilterChangeInternal = (value: FilterValue) => {
    setFilterValue(value);
  };

  useEffect(() => {
    if (showFilterContent) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [showFilterContent]);

  useEffect(() => {
    if (!showFilterContent) {
      document.addEventListener('openFilterPopup', (e: CustomEvent) => {
        if (e.detail === filterOption.filterId) setShowFilterContent(true);
      });
    }
  }, []);

  useEffect(() => {
    if (filterOption.filterType !== 'select') return;
    if (!Array.isArray(filterValue)) return;
    const invalidOptions = filterValue.filter(
      (val) =>
        !filterOption.filterOptions?.some((option) => option.value === val) &&
        filterOption.includeUnassigned &&
        val !== 'unassigned'
    );
    if (invalidOptions.length) {
      onFilterChange(
        filterOption.filterId,
        filterValue.filter((val) => !invalidOptions.includes(val))
      );
    }
  }, [filterValue, filterOption.filterOptions]);

  const filterContent = useMemo(() => {
    switch (filterOption.filterType) {
      case 'text':
        return (
          <TextFilterContent
            filterOption={filterOption}
            onFilterChange={onFilterChangeInternal}
            value={(filterValue || '') as string}
            inputRef={inputRef}
          />
        );
      case 'select':
        return (
          <MultiSelectFilterContent
            filterOption={filterOption}
            onFilterChange={onFilterChangeInternal}
            value={(filterValue || []) as string[]}
            inputRef={inputRef}
          />
        );
      case 'dateRange':
        return (
          <DateRangeSelectFilterContent
            filterOption={filterOption}
            onFilterChange={onFilterChangeInternal}
            value={filterValue as DateRangeValue}
          />
        );
      case 'numberRange':
        return (
          <NumberRangeFilterContent
            filterOption={filterOption}
            onFilterChange={onFilterChangeInternal}
            value={filterValue as NumberRangeValue}
            inputRef={inputRef}
          />
        );
      default:
        return <></>;
    }
  }, [filterOption.filterType, filterValue, inputRef, filterOption.filterOptions]);

  return (
    <div key={filterOption.filterId}>
      <Popover
        closeOnBlur
        closeOnEsc
        onClose={handleApplyFilter}
        isOpen={showFilterContent}
        placement="bottom-end"
      >
        <PopoverTrigger>
          <div className="border border-gray-200 rounded text-gray-600 text-sm">
            <button className="py-4 px-12" onClick={handleToggleFilterPopup}>
              <div className="flex items-center gap-4">
                <span>{filterOption.filterLabel}</span>
                <span className="text-gray-800 font-bold">
                  <FilterValueRenderer value={value} filterOption={filterOption} />
                </span>
              </div>
            </button>
            <Tippy content="Clear filter">
              <button
                className="px-10 py-4 border-l"
                onClick={onFilterClear.bind(this, filterOption.filterId)}
              >
                <CloseIcon size={12} />
              </button>
            </Tippy>
          </div>
        </PopoverTrigger>
        <PopoverContent>
          <div className="p-12 min-w-200 bg-white rounded border border-gray-200 relative">
            {filterContent}
            <div className="flex items-center pt-12 gap-8">
              <button
                className="text-gray-600 flex-grow text-sm px-8 py-4 border border-gray-500 rounded"
                onClick={handleApplyFilter}
              >
                Apply
              </button>
              <button
                className="text-gray-600 flex-grow text-sm px-8 py-4 border border-gray-500 rounded"
                onClick={() => setShowFilterContent(false)}
              >
                Cancel
              </button>
            </div>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
};

const TextFilterContent = ({
  filterOption,
  onFilterChange,
  value,
  inputRef,
}: {
  filterOption: FilterOption;
  onFilterChange: (value: FilterValue) => void;
  value: string;
  inputRef?: React.RefObject<HTMLInputElement>;
}) => {
  return (
    <input
      className="px-4 py-2 border border-gray-200 rounded"
      type="text"
      placeholder={filterOption.searchPlaceholder || 'Search'}
      value={value}
      onChange={(e) => onFilterChange(e.target.value)}
      ref={inputRef}
    />
  );
};

const MultiSelectFilterContent = ({
  filterOption,
  onFilterChange,
  value = [],
  inputRef,
}: {
  filterOption: FilterOption;
  onFilterChange: (value: FilterValue) => void;
  value: string[];
  inputRef?: React.RefObject<HTMLInputElement>;
}) => {
  const filterSelectOptions = filterOption.filterOptions || [];

  const [search, setSearch] = useState('');

  const filterSelectOptionsFiltered = useMemo(() => {
    return filterSelectOptions.filter((option) =>
      option.label.toLowerCase().includes(search.toLowerCase())
    );
  }, [search, filterSelectOptions]);

  const handleOptionSelect = (filterId: string, optionValue: string) => {
    if (value.includes(optionValue)) {
      onFilterChange(value.filter((op) => op !== optionValue));
    } else {
      onFilterChange([...value, optionValue]);
    }
  };

  const allSelected = filterOption.includeUnassigned
    ? value.length === filterSelectOptions.length + 1
    : value.length === filterSelectOptions.length;

  const handleSelectAll = () => {
    if (allSelected) {
      onFilterChange([]);
    } else {
      const filterValue = filterSelectOptions.map((option) => option.value);
      if (filterOption.includeUnassigned) filterValue.push('unassigned');
      onFilterChange(filterValue);
    }
  };

  return (
    <div className="flex flex-col gap-4">
      <div>
        <input
          ref={inputRef}
          className="px-4 py-2 border border-gray-200 rounded"
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={filterOption.searchPlaceholder || 'Search'}
        />
      </div>
      <div className="flex flex-col gap-4 max-h-450 overflow-x-auto">
        <div>
          <Checkbox checked={allSelected} onChange={handleSelectAll}>
            <span>Select all</span>
          </Checkbox>
        </div>
        {filterSelectOptionsFiltered.map((option) => {
          const isChecked = !!value?.some((x) => x === option.value);
          return (
            <div key={option.value.toString()}>
              <Checkbox
                checked={isChecked}
                onChange={handleOptionSelect.bind(this, filterOption.filterId, option.value)}
              >
                <div className="flex items-center gap-8">
                  {option.icon && <span>{option.icon}</span>}
                  <span>{option.label}</span>
                </div>
              </Checkbox>
            </div>
          );
        })}
        {filterOption.includeUnassigned && 'unassigned'.includes(search.toLowerCase()) && (
          <div>
            <Checkbox
              checked={value.includes('unassigned')}
              onChange={handleOptionSelect.bind(this, filterOption.filterId, 'unassigned')}
            >
              <span>{filterOption.unassignedText || 'Unassigned'}</span>
            </Checkbox>
          </div>
        )}
      </div>
      {!filterSelectOptionsFiltered.length &&
        (!filterOption.includeUnassigned ||
          (filterOption.includeUnassigned && !'unassigned'.includes(search.toLowerCase()))) && (
          <span className="text-gray-600 text-center text-sm">No options found</span>
        )}
    </div>
  );
};

const DateRangeSelectFilterContent = ({
  filterOption,
  onFilterChange,
  value: internalValue,
}: {
  filterOption: FilterOption;
  onFilterChange: (value: FilterValue) => void;
  value: DateRangeValue;
}) => {
  return (
    <div>
      <div className="flex gap-8">
        <div>
          <div className="flex items-center justify-between mb-4">
            <span>Start date:</span>
            {internalValue.startDate && (
              <button
                onClick={() => onFilterChange({ ...internalValue, startDate: null })}
                className="text-red-500"
              >
                Clear
              </button>
            )}
          </div>
          <Calendar
            value={internalValue.startDate ? [new Date(internalValue.startDate)] : []}
            disableEndDate
            onChange={([date]) => {
              onFilterChange({
                ...internalValue,
                startDate: date.toString(),
                endDate:
                  internalValue.endDate && date > new Date(internalValue.endDate)
                    ? date.toString()
                    : internalValue.endDate,
              });
            }}
          />
        </div>
        <div>
          <div className="flex items-center justify-between mb-4">
            <span>End date:</span>
            {internalValue.endDate && (
              <button
                onClick={() => onFilterChange({ ...internalValue, endDate: null })}
                className="text-red-500"
              >
                Clear
              </button>
            )}
          </div>
          <Calendar
            value={internalValue.endDate ? [new Date(internalValue.endDate)] : []}
            disableEndDate
            onChange={([date]) => {
              onFilterChange({
                ...internalValue,
                endDate: date.toString(),
                startDate:
                  internalValue.startDate && date < new Date(internalValue.startDate)
                    ? date.toString()
                    : internalValue.startDate,
              });
            }}
          />
        </div>
      </div>
      {filterOption.includeUnassigned && (
        <div className="mt-12">
          <Checkbox
            checked={internalValue.showUnassigned}
            onChange={() =>
              onFilterChange({
                ...internalValue,
                showUnassigned: !internalValue.showUnassigned,
              })
            }
          >
            <span>{filterOption.unassignedText || 'Show unassigned'}</span>
          </Checkbox>
        </div>
      )}
    </div>
  );
};

const NumberRangeFilterContent = ({
  filterOption,
  onFilterChange,
  value,
  inputRef,
}: {
  filterOption: FilterOption;
  onFilterChange: (value: FilterValue) => void;
  value: NumberRangeValue;
  inputRef?: React.RefObject<HTMLInputElement>;
}) => {
  return (
    <div className="flex gap-8">
      <div>
        <div className="flex items-center justify-between mb-4">
          <span>Min:</span>
          {value.min !== null && (
            <button
              onClick={() => onFilterChange({ min: null, max: value.max })}
              className="text-red-500"
            >
              Clear
            </button>
          )}
        </div>
        <input
          ref={inputRef}
          className="px-4 py-2 border border-gray-200 rounded"
          type="number"
          placeholder="Min"
          min={filterOption.includeNegative ? undefined : 0}
          value={value.min || ''}
          onKeyPress={(e) => {
            if (e.key === '-' && !filterOption.includeNegative) e.preventDefault();
          }}
          onChange={(e) => {
            onFilterChange({
              min: e.target.value ? parseInt(e.target.value) : null,
              max: value.max,
            });
          }}
        />
      </div>
      <div>
        <div className="flex items-center justify-between mb-4">
          <span>Max:</span>
          {value.max !== null && (
            <button
              onClick={() => onFilterChange({ min: value.min, max: null })}
              className="text-red-500"
            >
              Clear
            </button>
          )}
        </div>
        <input
          className={classNames('px-4 py-2 border border-gray-200 rounded')}
          type="number"
          placeholder="Max"
          min={filterOption.includeNegative ? undefined : 0}
          value={value.max || ''}
          onKeyPress={(e) => {
            if (e.key === '-' && !filterOption.includeNegative) e.preventDefault();
          }}
          onChange={(e) => {
            onFilterChange({
              min: value.min,
              max: e.target.value ? parseInt(e.target.value) : null,
            });
          }}
        />
      </div>
    </div>
  );
};

const FilterValueRenderer = ({
  value,
  filterOption,
}: {
  value: FilterValue;
  filterOption: FilterOption;
}) => {
  if (!value) return <span>Any</span>;
  if (filterOption.filterType === 'select') {
    if (
      Array.isArray(value) &&
      filterOption.filterOptions &&
      ((filterOption.includeUnassigned &&
        value.length === filterOption.filterOptions?.length + 1) ||
        (!filterOption.includeUnassigned && value.length === filterOption.filterOptions?.length))
    ) {
      return <span>Any</span>;
    }
    if (!Array.isArray(value) || !value.length) {
      return <span>None</span>;
    }

    if (value.length > 3) return <span>{value.length} selected</span>;
    return (
      <div>
        {value.map((val, index) => {
          const option = filterOption.filterOptions?.find((op) => op.value === val);
          if (!option && val !== 'unassigned') return <></>;
          return (
            <span key={val.toString()}>
              {index !== 0 && ', '}
              {val === 'unassigned' ? 'Unassigned' : option?.label || val}
            </span>
          );
        })}
      </div>
    );
  }
  if (filterOption.filterType === 'dateRange') {
    const dateValue = value as DateRangeValue;
    if (!value || (!dateValue.endDate && !dateValue.startDate)) return <span>Any</span>;

    const formatDate = (date: string) => {
      const dateObj = new Date(date);
      return DateFns.format(dateObj, 'dd MMM yyyy');
    };

    if (dateValue.startDate && !dateValue.endDate)
      return <span>After {formatDate(dateValue.startDate)}</span>;

    if (dateValue.endDate && !dateValue.startDate)
      return <span>Before {formatDate(dateValue.endDate)}</span>;

    if (dateValue.startDate && dateValue.endDate)
      if (dateValue.startDate === dateValue.endDate)
        return <span>{formatDate(dateValue.startDate)}</span>;
      else
        return (
          <span>
            {formatDate(dateValue.startDate)} -&gt; {formatDate(dateValue.endDate)}
          </span>
        );
    return <></>;
  }
  if (filterOption.filterType === 'numberRange') {
    const numberValue = value as NumberRangeValue;
    if (!value || (isNil(numberValue.min) && isNil(numberValue.max))) return <span>Any</span>;

    if (!isNil(numberValue.min) && isNil(numberValue.max))
      return <span>Greater than {numberValue.min}</span>;

    if (isNil(numberValue.min) && !isNil(numberValue.max))
      return <span>Less than {numberValue.max}</span>;

    if (numberValue.min === numberValue.max) return <span>Exactly {numberValue.min}</span>;

    return (
      <span>
        {numberValue.min} - {numberValue.max}
      </span>
    );
  }
  if (typeof value === 'string' && value.length > 18) return <span>{value.slice(0, 15)}...</span>;
  return <span>{value}</span>;
};

const FilterIcon = ({ filterOption }: { filterOption: FilterOption }) => {
  if (!!filterOption.filterIcon as React.ReactNode) return <>{filterOption.filterIcon}</>;

  if (filterOption.filterType === 'text') return <TextIcon size={16} />;
  if (filterOption.filterType === 'select') return <SelectIcon size={16} />;
  if (filterOption.filterType === 'dateRange' || filterOption.filterType === 'date')
    return <CalendarIcon size={16} />;
  if (filterOption.filterType === 'numberRange' || filterOption.filterType === 'number')
    return <NumberIcon size={16} />;

  return <MyTaskIcon size={16} />;
};
